import { 
  Component, 
  OnInit,
  OnDestroy,
  OnChanges,
  Input,
  SimpleChanges,
  Output,
  EventEmitter,
  Inject,
  forwardRef
} from '@angular/core';

import { 
  FormGroup,
  Validators 
} from '@angular/forms';

import { isEqual, isNil } from 'lodash-es';
import { Subject } from 'rxjs';
import { takeUntil, mergeMap, shareReplay } from 'rxjs/operators';

import { DatexFormControl, validateControlOnChange, validateFormOnControlChange } from './models/datex-form-control';
import { 
  TextBoxModel, 
  NumberBoxModel, 
  SelectBoxModel, 
  ESelectBoxType,
  DateBoxModel, 
  CheckBoxModel, 
  TextModel, 
  LabelModel, 
  ButtonModel,
  SplitButtonModel,
  SeparatorModel,
  ImageModel,
  DrawModel,
  CodeBoxModel,
  ButtonStyles,
  ValueControlModel
} from './models/control';
import { Styles, ControlContainerStyles } from './models/style';
import { FieldModel } from './models/field';
import { FieldsetModel } from './models/fieldset';
import { ToolModel } from './models/tool';
import { BaseComponent } from './components/base.component';

import { SharedModule } from './shared.module';

import { UtilsService } from './utils.service';
import { SettingsValuesService } from './settings.values.service';
import { FootprintManager_ShellService } from './FootprintManager.shell.service';
import { FootprintManager_OperationService } from './FootprintManager.operation.service';
import { FootprintManager_DatasourceService } from './FootprintManager.datasource.index';
import { FootprintManager_FlowService } from './FootprintManager.flow.index';
import { FootprintManager_ReportService } from './FootprintManager.report.index';
import { FootprintManager_LocalizationService } from './FootprintManager.localization.service';
import { FootprintManager_plan_form_ComponentContextService } from './FootprintManager.plan_form.component.context.service';
import { Language } from './localization.service';
import { JobStatus } from './common-interfaces'
import { ApplicationType, ComponentType } from './app-context.service';
import { CleanupLoggerService } from './cleanup.logging.service';
import { $frontendTypes} from './FootprintManager.frontend.types'
import { $frontendTypes as $types} from './FootprintManager.frontend.types' 

import { EModalSize, EToasterType, EToasterPosition } from 'wavelength-ui';
import { Manufacturing_orders_dd_singleComponent } from './Manufacturing.orders_dd_single.component'

@Component({
  standalone: true,
  imports: [
    SharedModule,
    forwardRef(() => Manufacturing_orders_dd_singleComponent),
  ],
  selector: 'FootprintManager-plan_form',
  templateUrl: './FootprintManager.plan_form.component.html'
})
export class FootprintManager_plan_formComponent extends BaseComponent implements OnInit, OnDestroy {


  //#region Outputs
  @Output()
  $finish = new EventEmitter();
  //#endregion

  //#region title
  // Make it async so that it won't cause expressionChangedAfterItHasBeenCheckedError
  // The title is often meant to be shown from the parent (shell breadcrumb for example)
  // and often it will cause an expressionChangedAfterItHasBeenCheckedError because 
  // the parent has already been checked and the child now change something on the parent 
  // in dev, CD is run twice
  $titleChange = new EventEmitter<string>(true);
  private $_title: string;
  get title(): string {
    return this.$_title;
  }
  set title(t: string) {
    this.$_title = t;
    this.$titleChange.emit(this.$_title);
  }
  //#endregion title
  //#region Variables
  vars: { cron_expression?: string } = { };
  //#endregion
  //#region Events
  
  //#endregion

  formGroup: FormGroup = new FormGroup({
    is_active: new DatexFormControl(null, { validators: [  ], updateOn: 'change' }),
    name: new DatexFormControl(null, { validators: [ Validators.required ], updateOn: 'blur' }),
    template_order_id: new DatexFormControl(null, { validators: [ Validators.required ], updateOn: 'blur' }),
    total_runs: new DatexFormControl(null, { validators: [ Validators.required ], updateOn: 'blur' }),
    offset_in_days: new DatexFormControl(null, { validators: [ Validators.required ], updateOn: 'blur' }),
  });

  get valid(): boolean {
    return this.formGroup.valid;
  }
  
  toolbar = {
      create: new ToolModel(new ButtonModel('create', new ButtonStyles(['primary'], null), false, false, false, 'Create', '', null)
    , false),
      cancel: new ToolModel(new ButtonModel('cancel', new ButtonStyles(null, null), false, false, false, 'Cancel', '', null)
    , false)
  };

  fields = {
    is_active: new FieldModel(new CheckBoxModel(this.formGroup.controls['is_active'] as DatexFormControl, null, false, false, '', null)
, new ControlContainerStyles(null, null), 'Active', false, false),
    name: new FieldModel(new TextBoxModel(this.formGroup.controls['name'] as DatexFormControl, null, false, false, '', null)
, new ControlContainerStyles(null, null), 'Plan name', true, false),
    template_order_id: new FieldModel(new SelectBoxModel(
  this.formGroup.controls['template_order_id'] as DatexFormControl, 
  ESelectBoxType.dropdown, null,
  false,
  false,
  '', null)
, new ControlContainerStyles(null, null), 'Source manufacturing order', true, false),
    total_runs: new FieldModel(new NumberBoxModel(this.formGroup.controls['total_runs'] as DatexFormControl, null, false, false, '', '', null)
, new ControlContainerStyles(null, null), 'Runs daily for', true, false),
    offset_in_days: new FieldModel(new NumberBoxModel(this.formGroup.controls['offset_in_days'] as DatexFormControl, null, false, false, '', '', null)
, new ControlContainerStyles(null, null), 'Days offset', true, false),
    set_schedule: new FieldModel(new ButtonModel('', new ButtonStyles(['primary'], null), false, false, false, 'Set schedule', 'icon-ic_fluent_calendar_20_regular', null)
, new ControlContainerStyles(null, null), '', false, false),
  };

  fieldsets = {
    group_plan: new FieldsetModel(
      '',
      true,
      false,
      true,
      false,
        null
      ,
      () => {
        const $form = this;
        const $utils = this.$utils;
        return '';
      }
    ),
    plan_setup: new FieldsetModel(
      'Plan setup',
      false,
      false,
      true,
      false,
        null
      ,
      () => {
        const $form = this;
        const $utils = this.$utils;
        return '';
      }
    ),
    order_schedule: new FieldsetModel(
      'Order schedule',
      false,
      false,
      true,
      false,
        null
      ,
      () => {
        const $form = this;
        const $utils = this.$utils;
        return '';
      }
    ),
};

  //#region fields inParams
  //#endregion fields inParams

  $formGroupFieldValidationObservables = {
    is_active: this.fields.is_active.control.valueChanges
    ,
    name: this.fields.name.control.valueChanges
    ,
    template_order_id: this.fields.template_order_id.control.valueChanges
    ,
    total_runs: this.fields.total_runs.control.valueChanges
    ,
    offset_in_days: this.fields.offset_in_days.control.valueChanges
    ,
  }
  

  constructor(
    private $utils: UtilsService,
private $settings: SettingsValuesService,
private $shell: FootprintManager_ShellService,
private $datasources: FootprintManager_DatasourceService,
private $flows: FootprintManager_FlowService,
private $reports: FootprintManager_ReportService,
private $localization: FootprintManager_LocalizationService,
private $operations: FootprintManager_OperationService,
private $logger: CleanupLoggerService,
private $context: FootprintManager_plan_form_ComponentContextService,
) { 
    super();
    this.$subscribeFormControlValueChanges();
  }

  ngOnInit(): void {
    this.$init();
  }
  

  private $unsubscribe$ = new Subject();
  ngOnDestroy(): void {
    this.$unsubscribe$.next(null);
    this.$unsubscribe$.complete();
  }

  initialized = false;

  async $init() {
    this.title = 'Plan';
  
    const $form = this;
    const $utils = this.$utils;

    (this.fields.is_active.control as CheckBoxModel).reset(false);
    


    this.initialized = true;
  }

  private $subscribeFormControlValueChanges() {
    this.$formGroupFieldValidationObservables
      .is_active
      .pipe(takeUntil(this.$unsubscribe$))
      .subscribe();
    this.$formGroupFieldValidationObservables
      .name
      .pipe(takeUntil(this.$unsubscribe$))
      .subscribe();
    this.$formGroupFieldValidationObservables
      .template_order_id
      .pipe(takeUntil(this.$unsubscribe$))
      .subscribe();
    this.$formGroupFieldValidationObservables
      .total_runs
      .pipe(takeUntil(this.$unsubscribe$))
      .subscribe();
    this.$formGroupFieldValidationObservables
      .offset_in_days
      .pipe(takeUntil(this.$unsubscribe$))
      .subscribe();
  }

  close() {
    this.$finish.emit();
  }

  openImageViewer(imageSource: string) {
    this.$shell.openImageViewerDialog(imageSource);
  }
 
  //#region private flows
  set_schedule(event = null) {
    return this.set_scheduleInternal(
      this,
  this.$shell,
      this.$datasources,
      this.$flows,
      this.$reports,
      this.$settings,
      this.$operations,
      this.$utils,
      this.$context,
      // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
      // this.localization,
      event);
  }
  async set_scheduleInternal(
    $form: FootprintManager_plan_formComponent,
  
    $shell: FootprintManager_ShellService,
    $datasources: FootprintManager_DatasourceService,
    $flows: FootprintManager_FlowService,
    $reports: FootprintManager_ReportService,
    $settings: SettingsValuesService,
    $operations: FootprintManager_OperationService,
    $utils: UtilsService,
    $context: FootprintManager_plan_form_ComponentContextService,
    // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
    //$l10n: FootprintManager_LocalizationService,
    $event: any
  ) {
    this.$logger.log('FootprintManager', 'plan_form.set_schedule');
  let result = (await $shell.Utilities.openschedule_frequency_formDialog({
      schedule: null
  }, 'modal'));
  
  if (!$utils.isDefined(result.schedule?.date_interval) &&
      !$utils.isDefined(result.schedule?.date_option) &&
      !$utils.isDefined(result.schedule?.time_interval) &&
      !$utils.isDefined(result.schedule?.time_offset) &&
      !$utils.isDefined(result.schedule?.time_option) &&
      !$utils.isDefined(result.schedule?.time_zone)) {
      $form.vars.cron_expression = result.cron_expression
  }
  
  }
  create_plan(event = null) {
    return this.create_planInternal(
      this,
  this.$shell,
      this.$datasources,
      this.$flows,
      this.$reports,
      this.$settings,
      this.$operations,
      this.$utils,
      this.$context,
      // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
      // this.localization,
      event);
  }
  async create_planInternal(
    $form: FootprintManager_plan_formComponent,
  
    $shell: FootprintManager_ShellService,
    $datasources: FootprintManager_DatasourceService,
    $flows: FootprintManager_FlowService,
    $reports: FootprintManager_ReportService,
    $settings: SettingsValuesService,
    $operations: FootprintManager_OperationService,
    $utils: UtilsService,
    $context: FootprintManager_plan_form_ComponentContextService,
    // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
    //$l10n: FootprintManager_LocalizationService,
    $event: any
  ) {
    this.$logger.log('FootprintManager', 'plan_form.create_plan');
  alert(`CRON:'${$form.vars.cron_expression}'`);
  
  // $flows.Manufacturing.create_planning({
  //     cron_expression: $form.vars.cron_expression,
  //     name: $form.fields.name.control.value,
  //     rule_id: generateUniqueID(),
  //     template_order_id: $form.fields.template_order_id.control.value,
  //     offset_in_days: $form.fields.days_offset.control.value,
  //     total_runs: $form.fields.runs_daily_for.control.value
  // })
  
  function generateUniqueID(): string {
      const date = new Date();
      const dateTimeString = date.toISOString().replace(/[-:.]/g, '').slice(0, -5);
      const randomChars = Math.random().toString(36).substring(2, 6); // Generates 4 random alphanumeric characters
      return `${dateTimeString}-${randomChars}`;
  }
  
  }
  //#endregion private flows
}
